import { useEffect, useRef } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setTvMode } from "./redux/actions";
import {
  BOOKING_STORAGE_KEYS,
  GROUP_BOOKING_STORAGE_KEYS,
  setPersistedFilter,
} from "../../utils/store/bookingFilterPersistence";
import {TIMELINE_VIEW} from "../../constants";

const isTvModeParamEnabled = (value) => {
  if (!value) return false;
  return value === "1" || value.toLowerCase() === "true";
};

const persistTimelineViewForPath = (pathname) => {
  const path = pathname || "";

  if (path.startsWith('/group-views/')) {
    const groupName = decodeURIComponent(path.split('/')[2] || 'default').toLowerCase();
    setPersistedFilter(GROUP_BOOKING_STORAGE_KEYS.VIEW, TIMELINE_VIEW, { namespace: `group_view_${groupName}` });
  } else if (path.startsWith('/bookings-all')) {
    setPersistedFilter(BOOKING_STORAGE_KEYS.VIEW, TIMELINE_VIEW, { namespace: 'all_bookings' });
  } else if (path.startsWith('/bookings')) {
    setPersistedFilter(BOOKING_STORAGE_KEYS.VIEW, TIMELINE_VIEW, { namespace: 'my_bookings' });
  }

  // Legacy fallback writes for backward compatibility.
  setPersistedFilter(BOOKING_STORAGE_KEYS.VIEW, TIMELINE_VIEW);
  setPersistedFilter(GROUP_BOOKING_STORAGE_KEYS.VIEW, TIMELINE_VIEW);
};

const removeSearchParamIfNeeded = ({ removeParamAfterUse, params, urlParamName, search, history, pathname }) => {
  if (!removeParamAfterUse) return;

  params.delete(urlParamName);
  const next = params.toString();
  if (`?${next}` !== search && !(next === "" && search === "")) {
    history.replace({ pathname, search: next });
  }
};

export default function useTvMode({
  isTimelineView,
  calendarRef,
  intervalMs = 300000,
  onTick,
  deps = [],
  urlParamName = "tvMode",
  removeParamAfterUse = true,
}) {
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const isTvMode = useSelector((state) => state?.bookingInstruments?.isTvMode);

  const onTickRef = useRef(onTick);
  useEffect(() => {
    onTickRef.current = onTick;
  }, [onTick]);

  useEffect(() => {
    try {
      const search = location?.search || "";
      if (!search) return;
      const params = new URLSearchParams(search);
      const val = params.get(urlParamName);
      if (!isTvModeParamEnabled(val) || isTvMode) return;

      persistTimelineViewForPath(location?.pathname);
      dispatch(setTvMode(true));
      removeSearchParamIfNeeded({
        removeParamAfterUse,
        params,
        urlParamName,
        search,
        history,
        pathname: location.pathname,
      });
    } catch {}
  }, [location.search, isTvMode, urlParamName, removeParamAfterUse, dispatch, history, location.pathname]);

  const autoRefreshEnabled = Boolean(isTimelineView && isTvMode);
  const timerRef = useRef(null);

  useEffect(() => {
    const clear = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    if (!autoRefreshEnabled) {
      clear();
      return;
    }

    const tick = () => {
      try {
        if (typeof onTickRef.current === "function") onTickRef.current();
        const cal = calendarRef && calendarRef.current;
        if (cal && typeof cal.reload === "function") cal.reload();
      } finally {
        timerRef.current = window.setTimeout(tick, intervalMs);
      }
    };

    tick();
    return () => clear(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefreshEnabled, intervalMs, calendarRef, ...deps]);
}
